import { subscribeAppResume } from "./appResumeService";
import { apiRequest, API_BASE_URL, getAccessToken } from "./apiClient";
import {
  getDataSyncCapabilities,
  getDataSyncPresenceSnapshot,
  isDataSyncConnected,
} from "./dataSyncService";
import { getPresenceClientId } from "./presenceClientIdentity";

const HEARTBEAT_MS = 60 * 1000;
const PRESENCE_FAST_MS = 10 * 1000;
const PRESENCE_SLOW_MS = 30 * 1000;
const FAST_WINDOW_MS = 60 * 1000;

let heartbeatTimer = null;
let presenceTimer = null;
let startedAt = 0;
let activeFamilyId = null;
let currentUserUid = null;
let onPresenceUpdate = null;
let heartbeatPromise = null;
let presencePromise = null;
let ssePresenceEnabled = false;
let offlineSent = false;
let presenceByUid = new Map();
let presenceGeneration = 0;

const normalizeUid = (value) => String(value || "").trim();

function setGlobals(users = []) {
  try {
    const onlineIds = new Set();
    const lastActiveByUid = {};
    users.forEach((u) => {
      const uid = normalizeUid(u.uid || u.id || u.firebaseUid || u.userId);
      if (!uid) return;
      if (u.lastActiveAt || u.lastSeenAt) lastActiveByUid[uid] = u.lastActiveAt || u.lastSeenAt;
      if (u.isOnline === true || u.online === true) onlineIds.add(uid);
    });
    window.__fpCurrentUid = normalizeUid(currentUserUid);
    window.__fpOnlineUserIds = onlineIds;
    window.__fpLastActiveByUid = lastActiveByUid;
  } catch {}
}

function publishPresence(users = []) {
  setGlobals(users);
  if (typeof onPresenceUpdate === "function") onPresenceUpdate(users);
}

function replacePresence(users = []) {
  presenceByUid = new Map();
  users.forEach((user) => {
    const uid = normalizeUid(user?.uid || user?.id || user?.firebaseUid || user?.userId);
    if (uid) presenceByUid.set(uid, user);
  });
  publishPresence(Array.from(presenceByUid.values()));
}

function patchPresence(user) {
  const uid = normalizeUid(user?.uid || user?.id || user?.firebaseUid || user?.userId);
  if (!uid) return;
  presenceByUid.set(uid, { ...(presenceByUid.get(uid) || {}), ...user, uid });
  publishPresence(Array.from(presenceByUid.values()));
}

async function heartbeat() {
  if (!activeFamilyId || document.hidden) return;
  if (heartbeatPromise) return heartbeatPromise;
  const familyId = activeFamilyId;
  const generation = presenceGeneration;
  const request = apiRequest(`/families/${familyId}/presence/heartbeat`, {
    method: "POST",
    body: JSON.stringify({ clientId: getPresenceClientId() }),
  })
    .catch((e) => { console.warn("[presence] heartbeat hata:", e.message); })
    .finally(() => {
      if (presenceGeneration === generation && heartbeatPromise === request) heartbeatPromise = null;
    });
  heartbeatPromise = request;
  return request;
}

async function fetchPresence() {
  if (!activeFamilyId || document.hidden) return null;
  if (presencePromise) return presencePromise;
  const familyId = activeFamilyId;
  const generation = presenceGeneration;
  const request = apiRequest(`/families/${familyId}/presence/online`)
    .then((data) => {
      const users = Array.isArray(data.users) ? data.users : [];
      if (presenceGeneration === generation && activeFamilyId === familyId) replacePresence(users);
      return users;
    })
    .catch((e) => {
      console.warn("[presence] online liste hata:", e.message);
      return null;
    })
    .finally(() => {
      if (presenceGeneration === generation && presencePromise === request) presencePromise = null;
    });
  presencePromise = request;
  return request;
}

function stopNetworkTimers() {
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  if (presenceTimer) clearTimeout(presenceTimer);
  heartbeatTimer = null;
  presenceTimer = null;
}

function schedulePresencePoll() {
  if (presenceTimer) clearTimeout(presenceTimer);
  if (!activeFamilyId || document.hidden || (ssePresenceEnabled && isDataSyncConnected(activeFamilyId))) return;
  const elapsed = Date.now() - startedAt;
  const nextMs = elapsed < FAST_WINDOW_MS ? PRESENCE_FAST_MS : PRESENCE_SLOW_MS;
  const jitteredMs = Math.round(nextMs * (0.8 + (Math.random() * 0.4)));
  presenceTimer = setTimeout(async () => {
    await fetchPresence();
    schedulePresencePoll();
  }, jitteredMs);
}

function startLegacyFallback({ immediate = false } = {}) {
  if (!activeFamilyId || document.hidden) return;
  if (immediate) {
    heartbeat();
    fetchPresence();
  }
  if (!heartbeatTimer) heartbeatTimer = setInterval(heartbeat, HEARTBEAT_MS);
  if (!presenceTimer) schedulePresencePoll();
}

function sendOffline() {
  if (!activeFamilyId || offlineSent) return;
  offlineSent = true;
  const token = getAccessToken();
  const url = `${API_BASE_URL}/families/${activeFamilyId}/presence/offline`;
  try {
    // sendBeacon Authorization header taşıyamadığı için native/web fetch keepalive kullanıyoruz.
    fetch(url, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ clientId: getPresenceClientId() }),
    }).catch(() => {});
  } catch {}
}

let cleanupVisibility = null;

export function startPresence({ familyId, user, onUpdate } = {}) {
  stopPresence({ offline: false });
  activeFamilyId = normalizeUid(familyId);
  currentUserUid = normalizeUid(user?.uid || user?.id);
  onPresenceUpdate = onUpdate;
  startedAt = Date.now();
  presenceByUid = new Map();
  offlineSent = false;
  ssePresenceEnabled = getDataSyncCapabilities(activeFamilyId)?.presenceV1 === true;

  if (!activeFamilyId || !currentUserUid) return () => stopPresence();

  const initialSnapshot = getDataSyncPresenceSnapshot(activeFamilyId);
  if (ssePresenceEnabled && Array.isArray(initialSnapshot?.users)) {
    replacePresence(initialSnapshot.users);
  }
  if (!ssePresenceEnabled || !isDataSyncConnected(activeFamilyId)) {
    startLegacyFallback({ immediate: true });
  }

  const onResume = () => {
    if (!activeFamilyId || document.hidden) return;
    offlineSent = false;
    // Arka planda aktif çağrı nedeniyle SSE açık kaldıysa aynı bağlantıyı tekrar
    // presence-aktif hâle getirir. Sağlıklı SSE'de periyodik heartbeat başlatmaz.
    heartbeat();
    if (!ssePresenceEnabled || !isDataSyncConnected(activeFamilyId)) {
      fetchPresence();
      startLegacyFallback();
    }
  };
  const onVisibility = () => {
    if (document.hidden) {
      stopNetworkTimers();
      sendOffline();
    }
  };
  const onPageHide = () => sendOffline();
  const onCapabilities = (event) => {
    const detail = event?.detail;
    if (String(detail?.familyId || "") !== String(activeFamilyId)) return;
    ssePresenceEnabled = detail?.capabilities?.presenceV1 === true;
    if (ssePresenceEnabled) {
      stopNetworkTimers();
      if (Array.isArray(detail?.presence?.users)) replacePresence(detail.presence.users);
    } else if (!document.hidden) {
      startLegacyFallback({ immediate: true });
    }
  };
  const onSseStatus = (event) => {
    const detail = event?.detail;
    if (String(detail?.familyId || "") !== String(activeFamilyId)) return;
    if (detail.connected === true && ssePresenceEnabled) {
      stopNetworkTimers();
    } else if (detail.connected !== true && !document.hidden) {
      startLegacyFallback({ immediate: true });
    }
  };
  const onDataChanged = (event) => {
    const detail = event?.detail;
    if (
      String(detail?.familyId || "") !== String(activeFamilyId)
      || detail?.resource !== "presence"
      || !detail?.item
    ) return;
    patchPresence(detail.item);
  };

  const unsubscribeResume = subscribeAppResume(onResume, { delayMs: 0 });
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("fp:sseCapabilities", onCapabilities);
  window.addEventListener("fp:sseStatus", onSseStatus);
  window.addEventListener("fp:dataChanged", onDataChanged);
  document.addEventListener("visibilitychange", onVisibility);

  cleanupVisibility = () => {
    if (typeof unsubscribeResume === "function") unsubscribeResume();
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("fp:sseCapabilities", onCapabilities);
    window.removeEventListener("fp:sseStatus", onSseStatus);
    window.removeEventListener("fp:dataChanged", onDataChanged);
    document.removeEventListener("visibilitychange", onVisibility);
  };

  return () => stopPresence();
}

export function stopPresence({ offline = true } = {}) {
  presenceGeneration += 1;
  stopNetworkTimers();
  if (cleanupVisibility) cleanupVisibility();
  cleanupVisibility = null;
  if (offline) sendOffline();
  activeFamilyId = null;
  currentUserUid = null;
  onPresenceUpdate = null;
  heartbeatPromise = null;
  presencePromise = null;
  ssePresenceEnabled = false;
  presenceByUid = new Map();
}

export function isUidOnline(uid) {
  const id = normalizeUid(uid);
  if (!id || id === normalizeUid(currentUserUid || window.__fpCurrentUid)) return false;
  return !!window.__fpOnlineUserIds?.has?.(id);
}

export function getLastActive(uid) {
  const id = normalizeUid(uid);
  return id ? (window.__fpLastActiveByUid?.[id] || null) : null;
}
